import React, { RefCallback, useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DropDownSubjectSearch } from "@/components/forms/inputs/dropDownSubjectSearch";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { SYSTEM_SUBJECT_ID } from "@/helper/systemIds";
import CrudFieldController from "@/components/tables/fields/CrudFieldController";
import { requiredRule } from "@/components/tables/fields/fieldRules";
import {
  CREATE_SED_FOR_COMPANY_FIELD_NAME,
  DEPARTMENT_SET_FIELD_NAME,
  STAFF_FIELD_NAME,
  SUBJECT_SET_FIELD_NAME,
} from "@/components/tables/fields/fieldNameRules";
import type { CrudModalField } from "@/components/tables/fields/crudModalField";
import styles from "@/components/tables/fields/crudField.module.css";

export interface CrudSubjectSearchFieldProps {
  field: Pick<
    CrudModalField,
    | "id"
    | "required"
    | "errorMessage"
    | "multiselect"
    | "setCompanyId"
    | "customLabel"
    | "allowSubjectSearchForNonAdmin"
    | "queryParameter"
    | "companyIdForFilteringAsync"
    | "asyncEntitySearch"
  >;
  entityOfTable?: string;
  validationTargetRef?: RefCallback<HTMLElement>;
  onChange?: (value: any) => void;
}

const NO_STATIC_OPTIONS: [] = []; 

const toSelectionOption = (subject: any) => ({
  value: subject.id ? subject.id : subject.value,
  label: subject.name ? subject.name : subject.label,
});

const CrudSubjectSearchField = ({
  field,
  entityOfTable,
  validationTargetRef,
  onChange,
}: CrudSubjectSearchFieldProps) => {
  const name = field.id as string;
  const { t } = useTranslation();
  const { control, getValues, setValue } = useFormContext();
  const staffAssigned = useWatch({ control, name: STAFF_FIELD_NAME });
  const selectedDepartmentSet = useWatch({
    control,
    name: DEPARTMENT_SET_FIELD_NAME,
  });
  const createSedForCompany = useWatch({
    control,
    name: CREATE_SED_FOR_COMPANY_FIELD_NAME,
  });
  const preselectedValue = useWatch({ control, name });
  const [preExistingSelection, setPreExistingSelection] = useState<any>("");
  const [preselectionApplied, setPreselectionApplied] = useState(false);

  // preserved quirk: the preselection is taken once, as soon as the form value is an object or a non-empty list
  useEffect(() => {
    if (field.asyncEntitySearch || preselectionApplied || !preselectedValue)
      return;
    if (field.multiselect) {
      if (!Array.isArray(preselectedValue) || preselectedValue.length === 0)
        return;
      const preselection = preselectedValue.map(toSelectionOption);
      setPreExistingSelection(preselection);
      setValue(name, preselection);
      setPreselectionApplied(true);
    } else if (
      typeof preselectedValue === "object" &&
      !Array.isArray(preselectedValue)
    ) {
      setPreExistingSelection([
        { value: preselectedValue?.id, label: preselectedValue?.name },
      ]);
      setPreselectionApplied(true);
    }
  }, [
    preselectedValue,
    preselectionApplied,
    field.multiselect,
    field.asyncEntitySearch,
  ]);

  const toStoredValue = (option: any) =>
    field.setCompanyId && option.companyId !== undefined
      ? option.companyId
      : option.value;

  const handleSelectionChange = (
    selectedOption: any,
    setFormValue: (value: any) => void
  ) => {
    if (field.multiselect) {
      if (Array.isArray(selectedOption) && selectedOption.length > 0) {
        const selection = selectedOption.map((option: any) => ({
          value: toStoredValue(option),
          label: option.label,
        }));
        onChange?.(selection);
        setFormValue(selection);
      } else {
        setPreExistingSelection([]);
        onChange?.([]);
        setFormValue([]);
      }
      return;
    }

    const singleOption = Array.isArray(selectedOption)
      ? selectedOption[0]
      : selectedOption;
    if (
      singleOption &&
      typeof singleOption === "object" &&
      singleOption.value
    ) {
      const storedValue = toStoredValue(singleOption);
      setFormValue(storedValue);
      onChange?.(storedValue);
      return;
    }

    setPreExistingSelection([]);
    setFormValue([]);
    onChange?.("");
  };

  const systemSubjectSelected =
    preExistingSelection.length !== 0 &&
    preExistingSelection[0]?.value !== undefined &&
    preExistingSelection[0]?.value === SYSTEM_SUBJECT_ID;
  const lockedByCompanyWideSed =
    entityOfTable === "subjectExaminationDeadline" &&
    field.id === SUBJECT_SET_FIELD_NAME &&
    (createSedForCompany === true || selectedDepartmentSet?.length !== 0); 
  const disabled =
    Boolean(staffAssigned) || systemSubjectSelected || lockedByCompanyWideSed;
  const subdomain = window.location.hostname.split(".")[0];

  return (
    <CrudFieldController
      name={name}
      control={control}
      rules={requiredRule(field.required, field.errorMessage)}
      validationTargetRef={validationTargetRef}
      className={styles.selectWrapper}
      render={({ field: { onChange: setFormValue, ref, ...rest }, error }) => (
        <>
          {field.customLabel !== "" && (
            <FormFieldLabel
              label={field.customLabel ?? t("subject")}
              htmlFor={name}
              mandatory={field.required}
            />
          )}
          <DropDownSubjectSearch
            {...rest}
            customRef={ref}
            allowSubjectSearchForNonAdmin={field.allowSubjectSearchForNonAdmin}
            wrapperClassName={styles.dropDownSubjectSearch}
            name={name}
            error={error}
            isMulti={field.multiselect}
            options={NO_STATIC_OPTIONS}
            preExistingValue={preExistingSelection}
            onChange={(selectedOption: any) =>
              handleSelectionChange(selectedOption, setFormValue)
            }
            subdomain={subdomain}
            queryParameter={field.queryParameter}
            companyIdforParent={getValues("id")}
            isClearable={true}
            useGlobalState={false}
            companyIdForFiltering={field.companyIdForFilteringAsync}
            disabled={disabled}
          />
        </>
      )}
    />
  );
};

export default CrudSubjectSearchField;
